import React, { useEffect, useState } from "react";
import { api, statusBadge, money } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Plus, X, Wand2, ListChecks, Circle, Clock, CheckCircle2, Loader2, Scale } from "lucide-react";

const canEstimate = (role) => ["admin", "manager", "estimator"].includes(role);
const inputCls = "rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

export const TicketSteps = ({ ticketId, role, onChange }) => {
  const { t } = useLang();
  const [tasks, setTasks] = useState([]);
  const [target, setTarget] = useState(0);
  const [newTask, setNewTask] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [newKind, setNewKind] = useState("labor");
  const [loading, setLoading] = useState(true);
  const [genLoading, setGenLoading] = useState(false);

  const refresh = async () => {
    const fresh = await api.get(`/tickets/${ticketId}`);
    setTasks(fresh.tasks || []);
    setTarget(fresh.estimate_total || 0);
    setLoading(false);
    onChange && onChange();
  };
  useEffect(() => { refresh(); }, [ticketId]); // eslint-disable-line

  const addTask = async () => {
    if (!newTask.trim()) return;
    await api.post(`/tickets/${ticketId}/tasks`, { title: newTask, kind: newKind, amount: parseFloat(newAmount) || 0 });
    setNewTask(""); setNewAmount("");
    refresh();
  };
  const cycleTask = async (tk) => {
    const next = { pending: "in_progress", in_progress: "done", done: "pending" }[tk.status];
    const res = await api.put(`/tickets/${ticketId}/tasks/${tk.id}`, { status: next });
    if (res?.all_done) toast.success(t(res.moved_to_receivables ? "checklistDoneMoved" : "checklistDone"));
    refresh();
  };
  const saveAmount = async (tk, val) => {
    const amt = parseFloat(val) || 0;
    if (amt === (tk.amount || 0)) return;
    await api.put(`/tickets/${ticketId}/tasks/${tk.id}`, { amount: amt });
    refresh();
  };
  const removeTask = async (taskId) => {
    await api.del(`/tickets/${ticketId}/tasks/${taskId}`);
    refresh();
  };
  const genTasks = async () => {
    setGenLoading(true);
    try {
      await api.post(`/tickets/${ticketId}/tasks/generate`);
      toast.success(t("saved"));
      await refresh();
    } catch { toast.error(t("error")); }
    finally { setGenLoading(false); }
  };

  const done = tasks.filter((x) => x.status === "done").length;
  const allocated = tasks.reduce((s, x) => s + (x.amount || 0), 0);
  const diff = Math.round((target - allocated) * 100) / 100;

  return (
    <div className="px-6 py-4 bg-slate-50/70">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-slate-500" />
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("jobSteps")}</span>
        </div>
        {canEstimate(role) && (
          <button data-testid={`inline-generate-steps-${ticketId}`} onClick={genTasks} disabled={genLoading} className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline disabled:opacity-60">
            {genLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} {t("generateSteps")}
          </button>
        )}
      </div>

      {/* Allocation vs estimate */}
      {(target > 0 || allocated > 0) && (
        <div data-testid={`steps-allocation-${ticketId}`} className="mb-3 bg-white border border-slate-200 rounded-lg p-3 max-w-md">
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5 text-slate-500"><Scale className="w-3.5 h-3.5" />{t("allocated")}</span>
            <span className="font-semibold text-slate-800 tabular-nums">{money(allocated)} / {money(target)}</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-2">
            <div className={`h-full transition-all duration-500 ${diff < 0 ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${target > 0 ? Math.min((allocated / target) * 100, 100) : 100}%` }} />
          </div>
          {diff > 0.009 && <p data-testid={`steps-toallocate-${ticketId}`} className="text-[11px] text-amber-600 mt-1.5 font-medium">{money(diff)} {t("toAllocate")}</p>}
          {diff < -0.009 && <p data-testid={`steps-over-${ticketId}`} className="text-[11px] text-red-600 mt-1.5 font-medium">{money(Math.abs(diff))} {t("overEstimate")}</p>}
        </div>
      )}

      {tasks.length > 0 && (
        <div className="mb-3 max-w-md">
          <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
            <span>{t("progress")}</span>
            <span className="font-semibold text-slate-700">{done}/{tasks.length}</span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${(done / tasks.length) * 100}%` }} />
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        {tasks.map((tk) => {
          const Icon = tk.status === "done" ? CheckCircle2 : tk.status === "in_progress" ? Clock : Circle;
          const color = tk.status === "done" ? "text-emerald-600" : tk.status === "in_progress" ? "text-blue-600" : "text-slate-300";
          return (
            <div key={tk.id} data-testid={`inline-task-${tk.id}`} className="flex items-center gap-2.5 group">
              <button data-testid={`inline-task-toggle-${tk.id}`} onClick={() => cycleTask(tk)} className={`${color} hover:scale-110 transition-transform shrink-0`}>
                <Icon className="w-5 h-5" />
              </button>
              <span className={`flex-1 min-w-0 text-sm truncate ${tk.status === "done" ? "line-through text-slate-400" : "text-slate-700"}`}>
                {tk.title}
                <span className={`ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${tk.kind === "material" ? "bg-cyan-50 text-cyan-600" : "bg-slate-100 text-slate-600"}`}>{t(tk.kind === "material" ? "material" : "labor")}</span>
                {tk.from_estimate === false && <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-600">{t("extraTag")}</span>}
              </span>
              {canEstimate(role) ? (
                <div className="flex items-center gap-0.5 shrink-0">
                  <span className="text-xs text-slate-400">$</span>
                  <input data-testid={`inline-task-amount-${tk.id}`} type="number" step="0.01" defaultValue={tk.amount || 0} onBlur={(e) => saveAmount(tk, e.target.value)}
                    className="w-20 rounded-md border border-slate-200 px-2 py-1 text-sm text-right tabular-nums focus:ring-2 focus:ring-slate-900 outline-none" />
                </div>
              ) : (
                <span className="text-sm font-medium text-slate-700 tabular-nums shrink-0">{money(tk.amount || 0)}</span>
              )}
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full shrink-0 ${statusBadge(tk.status)}`}>{t(tk.status)}</span>
              {canEstimate(role) && (
                <button onClick={() => removeTask(tk.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 transition-opacity shrink-0"><X className="w-4 h-4" /></button>
              )}
            </div>
          );
        })}
        {!loading && tasks.length === 0 && <p className="text-xs text-slate-400 py-2">{t("noSteps")}</p>}
      </div>

      {canEstimate(role) && (
        <div className="flex flex-wrap gap-2 mt-3 max-w-2xl">
          <input data-testid={`inline-new-task-${ticketId}`} value={newTask} onChange={(e) => setNewTask(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTask())} placeholder={t("addStep")} className={`${inputCls} flex-1 min-w-[160px]`} />
          <select data-testid={`inline-new-kind-${ticketId}`} value={newKind} onChange={(e) => setNewKind(e.target.value)} className={inputCls}>
            <option value="labor">{t("labor")}</option>
            <option value="material">{t("material")}</option>
          </select>
          <input data-testid={`inline-new-amount-${ticketId}`} type="number" step="0.01" value={newAmount} onChange={(e) => setNewAmount(e.target.value)} placeholder="$" className={`${inputCls} w-24`} />
          <button data-testid={`inline-add-task-${ticketId}`} onClick={addTask} className="rounded-lg px-3 py-2 text-sm font-medium bg-slate-100 hover:bg-slate-200 text-slate-700"><Plus className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
};
